import React, { useState, useRef, useCallback } from 'react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Plus, MapPin, Trash2, X, Pencil, Save, Link2, Camera, Search, Flag, ExternalLink } from 'lucide-react';

const NOMINATIM_DELAY = 400;
let searchTimeout = null;

const AddDestinationForm = ({ destinations, onAddDestination, onRemoveDestination, onUpdateDestination, accentColor }) => {
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [name, setName] = useState('');
  const [address, setAddress] = useState('');
  const [url, setUrl] = useState('');
  const [imageUrl, setImageUrl] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [isGeocoding, setIsGeocoding] = useState(false);
  const [geocodeWarning, setGeocodeWarning] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const photoInputRef = useRef(null);
  const searchRef = useRef(null);

  const searchNominatim = useCallback(async (query) => {
    if (!query || query.length < 3) { setSuggestions([]); return; }
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(query)}&limit=5&addressdetails=1`,
        { headers: { 'User-Agent': 'ParkingManagementApp/1.0' } }
      );
      const results = await res.json();
      setSuggestions(results.map(r => ({
        display: r.display_name,
        lat: parseFloat(r.lat),
        lng: parseFloat(r.lon),
        name: r.name || r.display_name.split(',')[0],
      })));
      setShowSuggestions(true);
    } catch {
      setSuggestions([]);
    }
  }, []);

  const handleSearchChange = (value) => {
    setAddress(value);
    setGeocodeWarning('');
    setCoordinates(null);
    if (searchTimeout) clearTimeout(searchTimeout);
    searchTimeout = setTimeout(() => searchNominatim(value), NOMINATIM_DELAY);
  };

  const selectSuggestion = (sug) => {
    setAddress(sug.display);
    if (!name) setName(sug.name);
    setCoordinates({ lat: sug.lat, lng: sug.lng });
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handlePhotoUpload = (e) => {
    const file = e.target.files[0];
    if (file && file.type.startsWith('image/')) {
      const img = new Image();
      img.onload = () => {
        const MAX_DIM = 600;
        let w = img.width, h = img.height;
        if (w > MAX_DIM || h > MAX_DIM) {
          const ratio = Math.min(MAX_DIM / w, MAX_DIM / h);
          w = Math.round(w * ratio);
          h = Math.round(h * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = w;
        canvas.height = h;
        canvas.getContext('2d').drawImage(img, 0, 0, w, h);
        setImageUrl(canvas.toDataURL('image/jpeg', 0.5));
      };
      img.src = URL.createObjectURL(file);
    }
  };

  const geocodeAddress = async (addr) => {
    try {
      const res = await fetch(
        `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(addr)}&limit=1`,
        { headers: { 'User-Agent': 'ParkingManagementApp/1.0' } }
      );
      const results = await res.json();
      if (results?.length > 0) return { lat: parseFloat(results[0].lat), lng: parseFloat(results[0].lon) };
    } catch { /* ignore */ }
    return null;
  };

  const startEdit = (dest) => {
    setEditingId(dest.id);
    setName(dest.name);
    setAddress(dest.address);
    setUrl(dest.url || '');
    setImageUrl(dest.imageUrl || '');
    setCoordinates(dest.coordinates);
    setGeocodeWarning('');
    setShowForm(true);
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setName('');
    setAddress('');
    setUrl('');
    setImageUrl('');
    setCoordinates(null);
    setGeocodeWarning('');
    setSuggestions([]);
    setShowSuggestions(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !address.trim()) return;

    setIsGeocoding(true);
    let coords = coordinates;

    if (!coords) {
      coords = await geocodeAddress(address);
    }
    if (!coords) {
      setGeocodeWarning('Could not find coordinates. Try searching for a more specific address.');
      setIsGeocoding(false);
      return;
    }

    const destData = {
      name: name.trim(),
      address: address.trim(),
      coordinates: coords,
      url: url.trim(),
      imageUrl,
    };

    if (editingId) {
      onUpdateDestination({ ...destData, id: editingId });
    } else {
      onAddDestination({ ...destData, id: `dest-${Date.now()}-${Math.random().toString(36).substr(2, 6)}` });
    }
    cancelForm();
    setIsGeocoding(false);
  };

  const btnColor = accentColor || '#3b82f6';

  return (
    <div className="space-y-4">
      {destinations.length > 0 && (
        <div className="space-y-2">
          <h4 className="text-sm font-medium text-slate-700">Destinations ({destinations.length})</h4>
          <div className="space-y-2 max-h-60 overflow-y-auto">
            {destinations.map((dest) => (
              <div key={dest.id} className={`flex items-center justify-between rounded-lg p-3 border transition-colors ${editingId === dest.id ? 'bg-blue-50 border-blue-300' : 'bg-slate-50 border-slate-200'}`} data-testid={`destination-item-${dest.id}`}>
                <div className="flex items-center space-x-2.5 flex-1 min-w-0">
                  {dest.imageUrl ? (
                    <img src={dest.imageUrl} alt={dest.name} className="w-10 h-10 rounded-lg object-cover flex-shrink-0" />
                  ) : (
                    <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center flex-shrink-0">
                      <Flag className="w-4 h-4 text-indigo-600" />
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-800 truncate">{dest.name}</div>
                    <div className="text-xs text-slate-500 truncate">{dest.address}</div>
                    {dest.url && (
                      <a href={dest.url} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-500 hover:underline flex items-center gap-0.5" onClick={e => e.stopPropagation()}>
                        <ExternalLink className="w-2.5 h-2.5" />Website
                      </a>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-1 ml-2">
                  <button onClick={() => startEdit(dest)} className="p-1.5 text-slate-400 hover:text-blue-600 rounded hover:bg-blue-50" title="Edit" data-testid={`dest-edit-${dest.id}`}><Pencil className="w-3.5 h-3.5" /></button>
                  <button onClick={() => onRemoveDestination(dest.id)} className="p-1.5 text-slate-400 hover:text-red-500 rounded hover:bg-red-50" title="Remove" data-testid={`dest-remove-${dest.id}`}><X className="w-3.5 h-3.5" /></button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {!showForm ? (
        <Button onClick={() => { setEditingId(null); setShowForm(true); }} variant="outline" className="w-full border-2 border-dashed border-indigo-200 hover:border-indigo-400 hover:bg-indigo-50 text-indigo-600 hover:text-indigo-700 py-5" data-testid="add-destination-btn">
          <Plus className="w-4 h-4 mr-2" />Add Destination
        </Button>
      ) : (
        <Card className="border-2 border-indigo-200 bg-indigo-50/30">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Flag className="w-4 h-4 text-indigo-600" />
                {editingId ? 'Edit Destination' : 'Add Destination'}
              </CardTitle>
              <button onClick={cancelForm} className="p-1 text-slate-400 hover:text-slate-600"><X className="w-4 h-4" /></button>
            </div>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* Search / Address */}
              <div className="relative">
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5">
                  <Search className="w-3.5 h-3.5" /><span>Search Location</span>
                </label>
                <Input
                  ref={searchRef}
                  type="text"
                  placeholder="Search for a place, building, or address..."
                  value={address}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                  required
                  data-testid="dest-search-input"
                />
                {showSuggestions && suggestions.length > 0 && (
                  <div className="absolute z-50 w-full mt-1 bg-white border border-slate-200 rounded-lg shadow-lg max-h-48 overflow-y-auto" data-testid="dest-suggestions-dropdown">
                    {suggestions.map((sug, i) => (
                      <button
                        key={i}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-indigo-50 text-sm text-slate-700 border-b border-slate-100 last:border-0"
                        onMouseDown={() => selectSuggestion(sug)}
                        data-testid={`dest-suggestion-${i}`}
                      >
                        <div className="flex items-start gap-2">
                          <MapPin className="w-3.5 h-3.5 text-indigo-500 mt-0.5 flex-shrink-0" />
                          <span className="line-clamp-2">{sug.display}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
                {coordinates && (
                  <div className="flex items-center space-x-1.5 mt-1.5">
                    <Link2 className="w-3 h-3 text-green-600" />
                    <span className="text-xs text-green-600 font-medium">Located: {coordinates.lat.toFixed(4)}, {coordinates.lng.toFixed(4)}</span>
                  </div>
                )}
                {geocodeWarning && (
                  <div className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded-md">
                    <p className="text-xs text-amber-700 font-medium">{geocodeWarning}</p>
                  </div>
                )}
              </div>

              {/* Name */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5">
                  <Flag className="w-3.5 h-3.5" /><span>Destination Name</span>
                </label>
                <Input
                  type="text"
                  placeholder="e.g., Main Library, Stadium Entrance"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  required
                  data-testid="dest-name-input"
                />
              </div>

              {/* URL */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-1.5">
                  <ExternalLink className="w-3.5 h-3.5" /><span>Website URL (optional)</span>
                </label>
                <Input
                  type="url"
                  placeholder="https://example.com"
                  value={url}
                  onChange={(e) => setUrl(e.target.value)}
                  data-testid="dest-url-input"
                />
              </div>

              {/* Photo */}
              <div>
                <label className="flex items-center space-x-1.5 text-sm font-medium text-slate-700 mb-2">
                  <Camera className="w-3.5 h-3.5" /><span>Image (optional)</span>
                </label>
                {imageUrl ? (
                  <div className="relative inline-block">
                    <img src={imageUrl} alt="Destination" className="h-24 w-auto rounded-lg border border-slate-200 object-cover" />
                    <button type="button" onClick={() => { setImageUrl(''); if (photoInputRef.current) photoInputRef.current.value = ''; }}
                      className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center hover:bg-red-600">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ) : (
                  <div>
                    <input ref={photoInputRef} type="file" accept="image/*" onChange={handlePhotoUpload} className="hidden" id="dest-photo" />
                    <label htmlFor="dest-photo" className="cursor-pointer flex items-center justify-center w-full h-16 border-2 border-dashed border-slate-300 rounded-lg hover:border-indigo-400 hover:bg-indigo-50 transition-colors">
                      <div className="text-center"><Camera className="w-5 h-5 text-slate-400 mx-auto" /><span className="text-xs text-slate-500">Upload image</span></div>
                    </label>
                  </div>
                )}
              </div>

              <div className="flex items-center space-x-2 pt-2">
                <Button type="submit" disabled={!name.trim() || !address.trim() || isGeocoding} className="flex-1 text-white" style={{ backgroundColor: btnColor }} data-testid="dest-submit-btn">
                  {isGeocoding ? (
                    <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />Locating...</>
                  ) : editingId ? (
                    <><Save className="w-4 h-4 mr-2" />Save Changes</>
                  ) : (
                    <><Plus className="w-4 h-4 mr-2" />Add Destination</>
                  )}
                </Button>
                <Button type="button" variant="outline" onClick={cancelForm}>Cancel</Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AddDestinationForm;
